// SPDX-License-Identifier: AGPL-3.0-or-later

import {ME} from '@fluxer/constants/src/AppConstants';

const replyParam = (reply?: string) => (reply ? `&reply=${encodeURIComponent(reply)}` : '');

export const Endpoints = {
	INSTANCE: '/.well-known/fluxer',
	AUTH_LOGIN: '/auth/login',
	/** KONVO: email + password sign-in, allowlisted accounts only (App Review). */
	KONVO_PASSWORD_LOGIN: '/konvo/auth/password-login',
	AUTH_LOGIN_MFA_TOTP: '/auth/login/mfa/totp',
	AUTH_LOGIN_MFA_WEBAUTHN_OPTIONS: '/auth/login/mfa/webauthn/authentication-options',
	AUTH_LOGIN_MFA_WEBAUTHN: '/auth/login/mfa/webauthn',
	AUTH_WEBAUTHN_OPTIONS: '/auth/webauthn/authentication-options',
	AUTH_WEBAUTHN_AUTHENTICATE: '/auth/webauthn/authenticate',
	AUTH_LOGOUT: '/auth/logout',
	AUTH_REGISTER: '/auth/register',
	AUTH_USERNAME_SUGGESTIONS: '/auth/username-suggestions',
	AUTH_SESSIONS: '/auth/sessions',
	AUTH_SESSIONS_LOGOUT: '/auth/sessions/logout',
	AUTH_HANDOFF_INITIATE: '/auth/handoff/initiate',
	AUTH_HANDOFF_COMPLETE: '/auth/handoff/complete',
	AUTH_HANDOFF_INFO: (code: string) => `/auth/handoff/${code}/info`,
	AUTH_HANDOFF_STATUS: (code: string) => `/auth/handoff/${code}/status`,
	AUTH_HANDOFF_CANCEL: (code: string) => `/auth/handoff/${code}`,
	AUTH_FORGOT_PASSWORD: '/auth/forgot',
	AUTH_RESET_PASSWORD: '/auth/reset',
	AUTH_VALIDATE_RESET_PASSWORD_TOKEN: (token: string) => `/auth/reset/${encodeURIComponent(token)}`,
	AUTH_EMAIL_REVERT: '/auth/email-revert',
	AUTH_VERIFY_EMAIL: '/auth/verify',
	AUTH_RESEND_VERIFICATION: '/auth/verify/resend',
	AUTH_AUTHORIZE_IP: '/auth/authorize-ip',
	AUTH_IP_AUTHORIZATION_RESEND: '/auth/ip-authorization/resend',
	AUTH_IP_AUTHORIZATION_POLL: (ticket: string) => {
		const params = new URLSearchParams({ticket});
		return `/auth/ip-authorization/poll?${params}`;
	},
	AUTH_SSO_START: '/auth/sso/start',
	AUTH_SSO_COMPLETE: '/auth/sso/complete',
	ADMIN_INSTANCE_CONFIG_GET: '/admin/instance-config/get',
	ADMIN_INSTANCE_CONFIG_UPDATE: '/admin/instance-config/update',
	ADMIN_INSTANCE_CONFIG_BRANDING_ASSET: '/admin/instance-config/branding-asset',
	ADMIN_INSTANCE_CONFIG_SMTP_TEST: '/admin/instance-config/integrations/smtp/test',
	SUDO_WEBAUTHN_OPTIONS: '/users/@me/sudo/webauthn/authentication-options',
	OAUTH_AUTHORIZE: '/oauth2/authorize',
	OAUTH_CONSENT: '/oauth2/authorize/consent',
	OAUTH_APPLICATIONS: '/oauth2/applications',
	OAUTH_APPLICATIONS_LIST: '/oauth2/applications/@me',
	OAUTH_APPLICATION: (applicationId: string) => `/oauth2/applications/${applicationId}`,
	OAUTH_APPLICATION_BOT_TOKEN_RESET: (applicationId: string) => `/oauth2/applications/${applicationId}/bot/reset-token`,
	OAUTH_APPLICATION_CLIENT_SECRET_RESET: (applicationId: string) =>
		`/oauth2/applications/${applicationId}/client-secret/reset`,
	OAUTH_APPLICATION_BOT_PROFILE: (applicationId: string) => `/oauth2/applications/${applicationId}/bot`,
	OAUTH_PUBLIC_APPLICATION: (applicationId: string) => `/oauth2/applications/${applicationId}/public`,
	OAUTH_AUTHORIZATIONS: '/oauth2/@me/authorizations',
	OAUTH_AUTHORIZATIONS_REVOKE: '/oauth2/@me/authorizations/revoke',
	OAUTH_AUTHORIZATION: (applicationId: string) => `/oauth2/@me/authorizations/${applicationId}`,
	CHANNEL: (channelId: string) => `/channels/${channelId}`,
	CHANNEL_ATTACHMENTS: (channelId: string) => `/channels/${channelId}/attachments`,
	CHANNEL_ATTACHMENTS_COMPLETE: (channelId: string) => `/channels/${channelId}/attachments/complete`,
	CHANNEL_INVITES: (channelId: string) => `/channels/${channelId}/invites`,
	CHANNEL_RECIPIENT: (channelId: string, userId: string) => `/channels/${channelId}/recipients/${userId}`,
	CHANNEL_MESSAGES: (channelId: string) => `/channels/${channelId}/messages`,
	CHANNEL_MESSAGES_BULK: '/channels/messages/bulk',
	CHANNEL_MESSAGE_SCHEDULE: (channelId: string) => `/channels/${channelId}/messages/schedule`,
	CHANNEL_MESSAGE: (channelId: string, messageId: string) => `/channels/${channelId}/messages/${messageId}`,
	CHANNEL_MESSAGE_ATTACHMENT: (channelId: string, messageId: string, attachmentId: string) =>
		`/channels/${channelId}/messages/${messageId}/attachments/${attachmentId}`,
	CHANNEL_MESSAGE_ACK: (channelId: string, messageId: string) => `/channels/${channelId}/messages/${messageId}/ack`,
	CHANNEL_MESSAGE_REACTION: (channelId: string, messageId: string, emoji: string) =>
		`/channels/${channelId}/messages/${messageId}/reactions/${emoji}`,
	CHANNEL_MESSAGE_REACTION_USERS: (channelId: string, messageId: string, emoji: string) =>
		`/channels/${channelId}/messages/${messageId}/reactions/${emoji}/users`,
	CHANNEL_MESSAGE_REACTION_QUERY: (channelId: string, messageId: string, emoji: string, query = ME) =>
		`/channels/${channelId}/messages/${messageId}/reactions/${emoji}/${query}`,
	CHANNEL_MESSAGE_REACTIONS: (channelId: string, messageId: string) =>
		`/channels/${channelId}/messages/${messageId}/reactions`,
	CHANNEL_MESSAGES_PURGE: (channelId: string) => `/channels/${channelId}/messages/purge`,
	CHANNEL_PIN: (channelId: string, messageId: string) => `/channels/${channelId}/pins/${messageId}`,
	CHANNEL_PINS: (channelId: string) => `/channels/${channelId}/messages/pins`,
	CHANNEL_PINS_ACK: (channelId: string) => `/channels/${channelId}/pins/ack`,
	CHANNEL_TYPING: (channelId: string) => `/channels/${channelId}/typing`,
	CHANNEL_WEBHOOKS: (channelId: string) => `/channels/${channelId}/webhooks`,
	CHANNEL_RTC_REGIONS: (channelId: string) => `/channels/${channelId}/rtc-regions`,
	CHANNEL_SLOWMODE: (channelId: string) => `/channels/${channelId}/slowmode`,
	CHANNEL_CALL: (channelId: string) => `/channels/${channelId}/call`,
	CHANNEL_CALL_RING: (channelId: string) => `/channels/${channelId}/call/ring`,
	CHANNEL_CALL_STOP_RINGING: (channelId: string) => `/channels/${channelId}/call/stop-ringing`,
	CHANNEL_VOICE_DEBUG_LOGGING_SESSION: (channelId: string) => `/channels/${channelId}/voice-debug-logging/session`,
	CHANNEL_VOICE_DEBUG_LOGGING_EVENTS: (channelId: string) => `/channels/${channelId}/voice-debug-logging/events`,
	CHANNEL_VOICE_PRESENCE_HEARTBEAT: (channelId: string) => `/channels/${channelId}/voice-presence/heartbeat`,
	GUILDS: '/guilds',
	GUILD: (guildId: string) => `/guilds/${guildId}`,
	GUILD_CHANNELS: (guildId: string) => `/guilds/${guildId}/channels`,
	GUILD_MEMBER: (guildId: string, query = ME) => `/guilds/${guildId}/members/${query}`,
	GUILD_MEMBERS: (guildId: string) => `/guilds/${guildId}/members`,
	GUILD_MEMBERS_SEARCH: (guildId: string) => `/guilds/${guildId}/members-search`,
	GUILD_MEMBER_ROLE: (guildId: string, userId: string, roleId: string) =>
		`/guilds/${guildId}/members/${userId}/roles/${roleId}`,
	GUILD_BAN: (guildId: string, userId: string) => `/guilds/${guildId}/bans/${userId}`,
	GUILD_BANS: (guildId: string) => `/guilds/${guildId}/bans`,
	GUILD_ROLE: (guildId: string, roleId: string) => `/guilds/${guildId}/roles/${roleId}`,
	GUILD_ROLES: (guildId: string) => `/guilds/${guildId}/roles`,
	GUILD_ROLE_HOIST_POSITIONS: (guildId: string) => `/guilds/${guildId}/roles/hoist-positions`,
	GUILD_DELETE: (guildId: string) => `/guilds/${guildId}/delete`,
	GUILD_TRANSFER_OWNERSHIP: (guildId: string) => `/guilds/${guildId}/transfer-ownership`,
	GUILD_EMOJI: (guildId: string, emojiId: string) => `/guilds/${guildId}/emojis/${emojiId}`,
	GUILD_EMOJIS: (guildId: string) => `/guilds/${guildId}/emojis`,
	GUILD_EMOJIS_CLONE: (guildId: string) => `/guilds/${guildId}/emojis/clone`,
	GUILD_STICKER: (guildId: string, stickerId: string) => `/guilds/${guildId}/stickers/${stickerId}`,
	GUILD_STICKERS: (guildId: string) => `/guilds/${guildId}/stickers`,
	GUILD_STICKERS_CLONE: (guildId: string) => `/guilds/${guildId}/stickers/clone`,
	EMOJI_METADATA: (emojiId: string) => `/emojis/${emojiId}/metadata`,
	STICKER_METADATA: (stickerId: string) => `/stickers/${stickerId}/metadata`,
	GUILD_INVITES: (guildId: string) => `/guilds/${guildId}/invites`,
	GUILD_VANITY_URL: (guildId: string) => `/guilds/${guildId}/vanity-url`,
	GUILD_WEBHOOKS: (guildId: string) => `/guilds/${guildId}/webhooks`,
	GUILD_AUDIT_LOGS: (guildId: string) => `/guilds/${guildId}/audit-logs`,
	INVITE: (code: string) => `/invites/${code}`,
	AGE_VERIFICATION: '/users/@me/age-verification',
	SWISH_AVAILABLE: '/swish/available',
	SWISH_PRICES: '/swish/prices',
	SWISH_CHECKOUT: '/swish/checkout',
	SWISH_PAYMENT: (paymentId: string) => `/swish/payments/${paymentId}`,
	SWISH_PAYMENTS: '/swish/payments',
	READ_STATES_ACK_BULK: '/read-states/ack-bulk',
	CHANNEL_READ_RECEIPTS: (channelId: string) => `/channels/${channelId}/read-receipts`,
	USER_READ_RECEIPTS: '/users/@me/read-receipts',
	KONVO_CALENDAR_EVENTS: '/konvo/calendar/events',
	KONVO_CALENDAR_PRESENCE: '/konvo/calendar/presence',
	KONVO_CALENDAR_FIND_TIMES: '/konvo/calendar/find-times',
	KONVO_CALENDAR_AVAILABILITY: '/konvo/calendar/availability',
	KONVO_CALENDARS: '/konvo/calendars',
	KONVO_CALENDAR_PREFS: '/konvo/calendar/prefs',
	KONVO_MONDAY_REPORT_PEOPLE: '/konvo/calendar/monday-report/people',
	KONVO_MONDAY_REPORT_PERSON: (userId: string) => `/konvo/calendar/monday-report/people/${encodeURIComponent(userId)}`,
	KONVO_CLICKUP_INBOX: '/konvo/clickup/inbox',
	KONVO_CLICKUP_INBOX_CLEAR_ALL: '/konvo/clickup/inbox/clear-all',
	KONVO_NOTIFICATION_PREFS: '/konvo/notifications/prefs',
	KONVO_NOTIFICATION_TEST: '/konvo/notifications/test',
	KONVO_TEAMS_MIRROR: (channelId: string) => `/konvo/teams/mirror/${channelId}`,
	KONVO_TEAMS_LINKS: '/konvo/teams/links',
	KONVO_TEAMS_MEETINGS_CONFIG: '/konvo/teams/meetings/config',
	KONVO_TEAMS_MEETINGS_TOKEN: '/konvo/teams/meetings/token',
	KONVO_TEAMS_MEETINGS_ASSIST: '/konvo/teams/meetings/assist',
	KONVO_TEAMS_MEETINGS_RECAP: '/konvo/teams/meetings/recap',
	KONVO_TEAMS_GROUPS: '/konvo/teams/groups',
	KONVO_TEAMS_GROUP_LINK: (chatId: string) => `/konvo/teams/groups/${encodeURIComponent(chatId)}/link`,
	KONVO_TEAMS_GROUP_MIRROR: (chatId: string) => `/konvo/teams/groups/${encodeURIComponent(chatId)}/mirror`,
	KONVO_WHATSAPP_STATUS: '/konvo/whatsapp/status',
	KONVO_WHATSAPP_LINK: '/konvo/whatsapp/link',
	KONVO_WHATSAPP_CHAT_SETTINGS: (jid: string) => `/konvo/whatsapp/chats/${encodeURIComponent(jid)}/settings`,
	KONVO_WHATSAPP_CONTACT_INFO: (jid: string) => `/konvo/whatsapp/chats/${encodeURIComponent(jid)}/info`,
	KONVO_WHATSAPP_CHATS: '/konvo/whatsapp/chats',
	KONVO_WHATSAPP_MESSAGES: (jid: string) => `/konvo/whatsapp/chats/${encodeURIComponent(jid)}/messages`,
	KONVO_WHATSAPP_MESSAGE: (jid: string, messageId: string) =>
		`/konvo/whatsapp/chats/${encodeURIComponent(jid)}/messages/${encodeURIComponent(messageId)}`,
	KONVO_WHATSAPP_MESSAGE_RECEIPTS: (jid: string, messageId: string) =>
		`/konvo/whatsapp/chats/${encodeURIComponent(jid)}/messages/${encodeURIComponent(messageId)}/receipts`,
	KONVO_WHATSAPP_POLLS: (jid: string) => `/konvo/whatsapp/chats/${encodeURIComponent(jid)}/polls`,
	KONVO_WHATSAPP_SEND_LOCATION: (jid: string) => `/konvo/whatsapp/chats/${encodeURIComponent(jid)}/location`,
	KONVO_WHATSAPP_SEND_CONTACTS: (jid: string) => `/konvo/whatsapp/chats/${encodeURIComponent(jid)}/contacts`,
	KONVO_WHATSAPP_CONTACTS: '/konvo/whatsapp/contacts',
	KONVO_WHATSAPP_VOTE: (jid: string, messageId: string) =>
		`/konvo/whatsapp/chats/${encodeURIComponent(jid)}/messages/${encodeURIComponent(messageId)}/vote`,
	KONVO_WHATSAPP_READ: (jid: string) => `/konvo/whatsapp/chats/${encodeURIComponent(jid)}/read`,
	KONVO_WHATSAPP_PRESENCE: (jid: string) => `/konvo/whatsapp/chats/${encodeURIComponent(jid)}/presence`,
	KONVO_WHATSAPP_ACTIVITY: '/konvo/whatsapp/activity',
	KONVO_WHATSAPP_AVATAR: (jid: string) => `/konvo/whatsapp/avatar/${encodeURIComponent(jid)}`,
	KONVO_WHATSAPP_SEND_MEDIA: (jid: string) => `/konvo/whatsapp/chats/${encodeURIComponent(jid)}/media`,
	KONVO_WHATSAPP_REACT: (jid: string) => `/konvo/whatsapp/chats/${encodeURIComponent(jid)}/react`,
	KONVO_WHATSAPP_AVATAR_IMAGE: (jid: string) => `/konvo/whatsapp/avatar/${encodeURIComponent(jid)}/image`,
	// reupload=1 asks the phone to re-upload media whose CDN link expired. That makes
	// the phone show a "Syncing with WhatsApp" notification, so it is only used for an
	// explicit retry, never on the render path.
	KONVO_WHATSAPP_MEDIA: (jid: string, messageId: string, reupload = false) =>
		`/konvo/whatsapp/media/${encodeURIComponent(jid)}/${encodeURIComponent(messageId)}${reupload ? '?reupload=1' : ''}`,
	KONVO_CALENDAR_EVENT_SERIES: (eventId: string) => `/konvo/calendar/events/${eventId}?scope=series`,
	KONVO_CALENDAR_EVENT: (eventId: string) => `/konvo/calendar/events/${encodeURIComponent(eventId)}`,
	KONVO_CALENDAR_EVENT_RESPOND: (eventId: string) => `/konvo/calendar/events/${encodeURIComponent(eventId)}/respond`,
	KONVO_MSGRAPH_CONNECT: '/konvo/msgraph/connect',
	KONVO_MSGRAPH_STATUS: '/konvo/msgraph/status',
	KONVO_MSGRAPH_DISCONNECT: '/konvo/msgraph/disconnect',
	KONVO_INTEGRATION_REQUIREMENTS: '/konvo/integrations/requirements',
	KONVO_CLICKUP_LISTS: '/konvo/clickup/lists',
	KONVO_CLICKUP_OAUTH_STATUS: '/konvo/clickup/oauth/status',
	KONVO_CLICKUP_OAUTH_CONNECT: '/konvo/clickup/oauth/connect',
	KONVO_CLICKUP_OAUTH_DISCONNECT: '/konvo/clickup/oauth/disconnect',
	KONVO_CLICKUP_MEMBERS: '/konvo/clickup/members',
	KONVO_CLICKUP_TASKS: '/konvo/clickup/tasks',
	KONVO_CLICKUP_TASK_DRAFT_CONTEXT: '/konvo/clickup/task-draft/context',
	KONVO_CLICKUP_MY_TASKS: '/konvo/clickup/me/tasks',
	KONVO_CLICKUP_HIERARCHY: '/konvo/clickup/hierarchy',
	KONVO_CLICKUP_DONE_STATUSES: '/konvo/clickup/done-statuses',
	KONVO_CLICKUP_WORKSPACE_TASKS: '/konvo/clickup/tasks',
	KONVO_CLICKUP_LIST: (listId: string) => `/konvo/clickup/list/${encodeURIComponent(listId)}`,
	KONVO_CLICKUP_SPACE_OVERVIEW: (spaceId: string) => `/konvo/clickup/space/${encodeURIComponent(spaceId)}/overview`,
	KONVO_CLICKUP_TASK: (taskId: string) => `/konvo/clickup/task/${encodeURIComponent(taskId)}`,
	KONVO_CLICKUP_TASK_UNDO_CREATE: (taskId: string) => `/konvo/clickup/task/${encodeURIComponent(taskId)}/undo-create`,
	KONVO_CLICKUP_TASK_COMMENTS: (taskId: string) => `/konvo/clickup/task/${encodeURIComponent(taskId)}/comments`,
	KONVO_CLICKUP_TASK_ATTACHMENTS: (taskId: string) => `/konvo/clickup/task/${encodeURIComponent(taskId)}/attachments`,
	KONVO_CLICKUP_TASK_TIME: (taskId: string) => `/konvo/clickup/task/${encodeURIComponent(taskId)}/time`,
	KONVO_CLICKUP_TASK_TIME_START: (taskId: string) => `/konvo/clickup/task/${encodeURIComponent(taskId)}/time/start`,
	KONVO_CLICKUP_TIME_CURRENT: '/konvo/clickup/time/current',
	KONVO_CLICKUP_TIME_STOP: '/konvo/clickup/time/stop',
	KONVO_CLICKUP_TIME_ENTRY: (entryId: string) => `/konvo/clickup/time/${encodeURIComponent(entryId)}`,
	KONVO_MAIL_RECENT: '/konvo/mail/recent',
	KONVO_MAIL_SEARCH_TOP: '/konvo/mail/search/top',
	KONVO_MAIL_SEARCH_SUGGEST: '/konvo/mail/search/suggest',
	KONVO_MAIL_MESSAGE: (messageId: string) => `/konvo/mail/messages/${encodeURIComponent(messageId)}`,
	KONVO_MAIL_CONVERSATION: (conversationId: string) =>
		`/konvo/mail/conversations/${encodeURIComponent(conversationId)}`,
	KONVO_MAIL_CONVERSATION_ATTACHMENTS: (conversationId: string) =>
		`/konvo/mail/conversations/${encodeURIComponent(conversationId)}/attachments`,
	KONVO_ASSISTANT_STATUS: '/konvo/assistant/status',
	KONVO_ASSISTANT_PROVIDER: '/konvo/assistant/provider',
	KONVO_ASSISTANT_POLICY: '/konvo/assistant/policy',
	KONVO_ASSISTANT_SETTINGS: '/konvo/assistant/settings',
	KONVO_ASSISTANT_CANCEL: '/konvo/assistant/cancel',
	KONVO_ASSISTANT_SPEECH: '/konvo/assistant/speech',
	KONVO_ASSISTANT_VOICE_TRANSCRIBE: '/konvo/assistant/voice/transcribe',
	KONVO_ASSISTANT_VOICE_END: '/konvo/assistant/voice/end',
	KONVO_ASSISTANT_VOICE_SCREEN: '/konvo/assistant/voice/screen',
	KONVO_ASSISTANT_VOICE_START: '/konvo/assistant/voice/start',
	KONVO_ASSISTANT_VOICE_ENGINES: '/konvo/assistant/voice/engines',
	KONVO_ASSISTANT_VOICE_CODEX_START: '/konvo/assistant/voice/codex/start',
	KONVO_ASSISTANT_VOICE_CODEX_EVENTS: '/konvo/assistant/voice/codex/events',
	KONVO_ASSISTANT_VOICE_CODEX_STOP: '/konvo/assistant/voice/codex/stop',
	KONVO_ASSISTANT_VOICE_LIVE_TOKEN: '/konvo/assistant/voice/live-token',
	KONVO_ASSISTANT_VOICE_ASK: '/konvo/assistant/voice/ask',
	KONVO_ASSISTANT_VOICE_LIVE_LOG: '/konvo/assistant/voice/live-log',
	KONVO_ASSISTANT_VOICE_GEMINI_FILLERS: '/konvo/assistant/voice/gemini-fillers',
	KONVO_ASSISTANT_VOICE_GEMINI_PREVIEW: '/konvo/assistant/voice/gemini-preview',
	KONVO_ASSISTANT_ENGINE: '/konvo/assistant/engine',
	/** "Waiting on a reply", both ways (konvo_api engine/waiting/WaitingController). */
	KONVO_ASSISTANT_WAITING: '/konvo/assistant/waiting',
	KONVO_ASSISTANT_WAITING_ACTION: (id: string) => `/konvo/assistant/waiting/${encodeURIComponent(id)}/action`,
	KONVO_ASSISTANT_WAITING_DRAFT: (id: string) => `/konvo/assistant/waiting/${encodeURIComponent(id)}/draft`,
	KONVO_ASSISTANT_WAITING_SEND: (id: string) => `/konvo/assistant/waiting/${encodeURIComponent(id)}/send`,
	KONVO_ASSISTANT_WRAP_UP: (id: string) => `/konvo/assistant/wrap-up/${encodeURIComponent(id)}`,
	KONVO_ASSISTANT_WRAP_UP_ACTION: (id: string, action: 'start' | 'skip' | 'undo') =>
		`/konvo/assistant/wrap-up/${encodeURIComponent(id)}/${action}`,
	/** "What Konvo knows": notes, summary and what the engine learned (KonvoAssistantKnowsController). */
	KONVO_ASSISTANT_KNOWS: '/konvo/assistant/knows',
	KONVO_ASSISTANT_KNOWS_NOTES: '/konvo/assistant/knows/notes',
	KONVO_ASSISTANT_KNOWS_NOTE: (noteId: string) => `/konvo/assistant/knows/notes/${encodeURIComponent(noteId)}`,
	KONVO_ASSISTANT_KNOWS_LEARNED_UNDO: (learnedId: string) =>
		`/konvo/assistant/knows/learned/${encodeURIComponent(learnedId)}/undo`,
	KONVO_ASSISTANT_KNOWS_HISTORY: '/konvo/assistant/knows/history',
	KONVO_ASSISTANT_CHAT: '/konvo/assistant/chat',
	KONVO_ASSISTANT_CHAT_NEW: '/konvo/assistant/chat/new',
	KONVO_ASSISTANT_CHAT_ARCHIVE: '/konvo/assistant/chat/archive',
	KONVO_ASSISTANT_CHAT_PIN: '/konvo/assistant/chat/pin',
	KONVO_ASSISTANT_CHAT_MUTE: '/konvo/assistant/chat/mute',
	KONVO_ASSISTANT_CHAT_HISTORY: '/konvo/assistant/chat/history',
	KONVO_ASSISTANT_CHAT_CONVERSATION: (id: string) => `/konvo/assistant/chat/conversations/${encodeURIComponent(id)}`,
	/** Action chips: what the user picked, for learning (docs/konvo-assistant/action-chips.md). */
	KONVO_ASSISTANT_ACTION_CHOICE: '/konvo/assistant/actions/choice',
	KONVO_ASSISTANT_USAGE: '/konvo/assistant/usage',
	KONVO_ASSISTANT_USAGE_EXPORT: '/konvo/assistant/usage/export',
	KONVO_ASSISTANT_USAGE_TEAM: '/konvo/assistant/usage/team',
	KONVO_ASSISTANT_ENGINE_OUTCOME: (itemId: string) =>
		`/konvo/assistant/engine/items/${encodeURIComponent(itemId)}/outcome`,
	KONVO_ASSISTANT_ENGINE_TODOS: (itemId: string) => `/konvo/assistant/engine/items/${encodeURIComponent(itemId)}/todos`,
	KONVO_ASSISTANT_ENGINE_TODO: (itemId: string, index: number) =>
		`/konvo/assistant/engine/items/${encodeURIComponent(itemId)}/todos/${index}`,
	KONVO_ASSISTANT_ENGINE_TODAY: '/konvo/assistant/engine/today',
	KONVO_ASSISTANT_DASHBOARDS: '/konvo/assistant/dashboards',
	KONVO_ASSISTANT_DASHBOARD: (id: string) => `/konvo/assistant/dashboards/${encodeURIComponent(id)}`,
	KONVO_ASSISTANT_DASHBOARD_DRILL: (id: string) => `/konvo/assistant/dashboards/${encodeURIComponent(id)}/drill`,
	KONVO_ASSISTANT_DASHBOARD_DELETE: (id: string) => `/konvo/assistant/dashboards/${encodeURIComponent(id)}/delete`,
	KONVO_ASSISTANT_DASHBOARD_SHARE: (id: string) => `/konvo/assistant/dashboards/${encodeURIComponent(id)}/share`,
	KONVO_ASSISTANT_DASHBOARD_SHOW: (id: string) => `/konvo/assistant/dashboards/${encodeURIComponent(id)}/show`,
	KONVO_ASSISTANT_DASHBOARD_SHARED: (shareId: string) =>
		`/konvo/assistant/dashboards/shares/${encodeURIComponent(shareId)}`,
	KONVO_ASSISTANT_DASHBOARD_PIN_ORDER: '/konvo/assistant/dashboards/pins/order',
	KONVO_ASSISTANT_ENGINE_FEEDBACK_EXPORT: '/konvo/assistant/engine/feedback-export',
	KONVO_CODEX_STATUS: '/konvo/codex/status',
	KONVO_CODEX_LINK_START: '/konvo/codex/link/start',
	KONVO_CODEX_LINK_STATUS: '/konvo/codex/link/status',
	KONVO_CODEX_LINK_CANCEL: '/konvo/codex/link/cancel',
	KONVO_CODEX_DISCONNECT: '/konvo/codex/disconnect',
	KONVO_CODEX_SETTINGS: '/konvo/codex/settings',
	KONVO_CODEX_RESET: '/konvo/codex/reset',
	KONVO_CLAUDE_STATUS: '/konvo/claude/status',
	KONVO_CLAUDE_RUNTIME: '/konvo/claude/runtime',
	KONVO_CLAUDE_CONNECT: '/konvo/claude/connect',
	KONVO_CLAUDE_DISCONNECT: '/konvo/claude/disconnect',
	KONVO_CLAUDE_SETTINGS: '/konvo/claude/settings',
	KONVO_CLAUDE_RESET: '/konvo/claude/reset',
	KONVO_CLAUDE_SUGGEST_TASK: '/konvo/claude/suggest-task',
	KONVO_CLAUDE_SUGGEST_ACTIONS: '/konvo/claude/suggest-actions',
	KONVO_CLAUDE_CHAT_SUGGESTIONS: '/konvo/claude/chat-suggestions',
	KONVO_CLAUDE_HOME_DIGEST: '/konvo/claude/home-digest',
	KONVO_CLAUDE_HOME_BRIEF: '/konvo/claude/home-brief',
	KONVO_CLAUDE_QUICK_REPLIES: '/konvo/claude/quick-replies',
	KONVO_CLAUDE_AGENT_REPLY: '/konvo/claude/agent-reply',
	KONVO_CLAUDE_HOME_CONTEXT: '/konvo/claude/home-context',
	KONVO_CLAUDE_LINK_START: '/konvo/claude/link/start',
	KONVO_CLAUDE_ONBOARDING_DISMISS: '/konvo/claude/onboarding/dismiss',
	KONVO_CLAUDE_LINK_CODE: '/konvo/claude/link/code',
	KONVO_CLAUDE_LINK_CANCEL: '/konvo/claude/link/cancel',
	KONVO_SEARCH: '/konvo/search',
	KONVO_FILES_DRIVE: (driveId: string, itemId: string) =>
		`/konvo/files/drive/${encodeURIComponent(driveId)}/${encodeURIComponent(itemId)}`,
	KONVO_DESKTOP_LATEST: '/konvo/desktop/latest',
	KONVO_MOBILE_LATEST: '/konvo/mobile/latest',
	KONVO_MOBILE_IOS_ENROLL: '/konvo/mobile/ios/enroll',
	KONVO_MOBILE_IOS_DEVICES: '/konvo/mobile/ios/devices',
	KONVO_MOBILE_WIDGETS_HOME: '/konvo/mobile/widgets/home',
	KONVO_SCRATCHPAD: '/konvo/scratchpad',
	KONVO_SCRATCHPAD_HISTORY: '/konvo/scratchpad/history',
	KONVO_SCRATCHPAD_RESTORE: '/konvo/scratchpad/restore',
	KONVO_CHANNEL_SCRATCHPAD: (channelId: string) => `/konvo/channels/${encodeURIComponent(channelId)}/scratchpad`,
	KONVO_BOARD: (boardId: string) => `/konvo/boards/${encodeURIComponent(boardId)}`,
	KONVO_BOARD_FILES: (boardId: string) => `/konvo/boards/${encodeURIComponent(boardId)}/files`,
	KONVO_BOARD_FILE: (boardId: string, fileId: string) =>
		`/konvo/boards/${encodeURIComponent(boardId)}/files/${encodeURIComponent(fileId)}`,
	KONVO_CHANNEL_BOARD: (channelId: string) => `/konvo/channels/${encodeURIComponent(channelId)}/board`,
	KONVO_CHANNEL_BOARD_FILES: (channelId: string) => `/konvo/channels/${encodeURIComponent(channelId)}/board/files`,
	KONVO_CHANNEL_BOARD_FILE: (channelId: string, fileId: string) =>
		`/konvo/channels/${encodeURIComponent(channelId)}/board/files/${encodeURIComponent(fileId)}`,
	KONVO_CHANNEL_BOARD_CURSOR: (channelId: string) => `/konvo/channels/${encodeURIComponent(channelId)}/board/cursor`,
	KONVO_BOARD_SKETCH: '/konvo/boards/sketch',
	KONVO_CHANNEL_SCRATCHPAD_LOCKS: (channelId: string) =>
		`/konvo/channels/${encodeURIComponent(channelId)}/scratchpad/locks`,
	KONVO_CHANNEL_SCRATCHPAD_CHANGES: (channelId: string) =>
		`/konvo/channels/${encodeURIComponent(channelId)}/scratchpad/changes`,
	KONVO_WEATHER: '/konvo/weather',
	KONVO_WEATHER_GEOCODE: '/konvo/weather/geocode',
	KONVO_CLICKUP_TIME_TODAY: '/konvo/clickup/time/today',
	KONVO_ASSISTANT_DM: '/konvo/assistant/dm',
	// KONVO: activity events for Team insights (batched by konvo_activity/state/Activity.ts).
	KONVO_ACTIVITY_EVENTS: '/konvo/activity/events',
	// KONVO: Team insights (staff only).
	KONVO_INSIGHTS_OVERVIEW: '/konvo/insights/overview',
	KONVO_INSIGHTS_PEOPLE: '/konvo/insights/people',
	KONVO_INSIGHTS_AGENT_PLANS: '/konvo/insights/agent-plans',
	KONVO_INSIGHTS_AGENT_USE: '/konvo/insights/agent-use',
	KONVO_INSIGHTS_PERSON: (userId: string) => `/konvo/insights/people/${encodeURIComponent(userId)}`,
	KONVO_INSIGHTS_SCHEDULE: '/konvo/insights/schedule',
	KONVO_INSIGHTS_SCHEDULE_SEND_NOW: (suggestionId: string) =>
		`/konvo/insights/schedule/${encodeURIComponent(suggestionId)}/send-now`,
	KONVO_INSIGHTS_SCHEDULE_SKIP: (suggestionId: string) =>
		`/konvo/insights/schedule/${encodeURIComponent(suggestionId)}/skip`,
	KONVO_INSIGHTS_SUGGESTIONS: '/konvo/insights/suggestions',
	KONVO_INSIGHTS_PERSON_CORNER: (userId: string) => `/konvo/insights/people/${encodeURIComponent(userId)}/corner`,
	KONVO_INSIGHTS_NUDGE_PREVIEW: '/konvo/insights/nudges/preview',
	KONVO_INSIGHTS_NUDGES: '/konvo/insights/nudges',
	KONVO_INSIGHTS_FEEDBACK: '/konvo/insights/feedback',
	KONVO_INSIGHTS_FEEDBACK_ASKS: '/konvo/insights/feedback/asks',
	KONVO_FEEDBACK_ASK: (askId: string) => `/konvo/feedback/asks/${encodeURIComponent(askId)}`,
	KONVO_FEEDBACK_ASK_ANSWERS: (askId: string) => `/konvo/feedback/asks/${encodeURIComponent(askId)}/answers`,
	KONVO_INSIGHTS_ROLLOUT: '/konvo/insights/rollout',
	KONVO_ENGAGEMENT_ACCESS: '/konvo/engagement/access',
	// KONVO: Agents, coding agents on your own machines (docs/konvo-agents/DESIGN.md).
	KONVO_INSIGHTS_ROLLOUT_AGENTS: '/konvo/insights/rollout/agents',
	KONVO_AGENTS: '/konvo/agents',
	KONVO_AGENTS_ACCESS: '/konvo/agents/access',
	KONVO_AGENTS_PAIRING: '/konvo/agents/pairing',
	KONVO_AGENTS_PREFS: '/konvo/agents/prefs',
	KONVO_AGENTS_UPLOADS: '/konvo/agents/uploads',
	KONVO_AGENTS_PULLS_LOOKUP: '/konvo/agents/pulls/lookup',
	KONVO_AGENTS_PULLS_OPEN: '/konvo/agents/pulls/open',
	KONVO_AGENTS_PULLS_REVIEW: '/konvo/agents/pulls/review',
	KONVO_AGENTS_PULLS_MERGE: '/konvo/agents/pulls/merge',
	KONVO_AGENTS_PULLS_CONVERSATION: '/konvo/agents/pulls/conversation',
	KONVO_AGENTS_PULLS_ACT: '/konvo/agents/pulls/act',
	KONVO_AGENTS_PULLS_ASK: '/konvo/agents/pulls/ask',
	KONVO_AGENTS_PULLS_INBOX: '/konvo/agents/pulls/inbox',
	KONVO_AGENTS_PULLS_DETAILS: '/konvo/agents/pulls/details',
	KONVO_AGENTS_PULLS_MEDIA: (repository: string, number: number, src: string) =>
		`/konvo/agents/pulls/media?repository=${encodeURIComponent(repository)}&number=${number}&src=${encodeURIComponent(src)}`,
	KONVO_AGENTS_RUNS: '/konvo/agents/runs',
	KONVO_AGENTS_RUNS_DETAIL: '/konvo/agents/runs/detail',
	KONVO_AGENTS_RUNS_ACT: '/konvo/agents/runs/act',
	KONVO_AGENTS_MERGE_ASKS: '/konvo/agents/merge-asks',
	KONVO_AGENTS_MERGE_ASKS_DISMISS: '/konvo/agents/merge-asks/dismiss',
	KONVO_AGENTS_MERGE_RIGHTS: '/konvo/agents/merge-rights',
	KONVO_AGENTS_COMMANDS: (machineId: string) => `/konvo/agents/machines/${machineId}/commands`,
	KONVO_AGENTS_USAGE_RESET: (machineId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/usage/reset`,
	KONVO_AGENTS_SETUP: (machineId: string) => `/konvo/agents/machines/${encodeURIComponent(machineId)}/setup`,
	KONVO_AGENTS_AGENT_UPDATE: (machineId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/agent-update`,
	KONVO_AGENTS_DEVICE_ACCESS: (machineId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/device-access`,
	KONVO_AGENTS_MACHINE_STATS: (machineId: string) => `/konvo/agents/machines/${encodeURIComponent(machineId)}/stats`,
	KONVO_AGENTS_ACTIVITY: (machineId: string) => `/konvo/agents/machines/${encodeURIComponent(machineId)}/activity`,
	KONVO_AGENTS_ACCOUNTS: (machineId: string) => `/konvo/agents/machines/${encodeURIComponent(machineId)}/accounts`,
	KONVO_AGENTS_TEAM_MACHINES: '/konvo/agents/team/machines',
	KONVO_AGENTS_TEAM_MACHINE_ACTIVITY: (machineId: string) =>
		`/konvo/agents/team/machines/${encodeURIComponent(machineId)}/activity`,
	KONVO_AGENTS_MACHINE: (machineId: string) => `/konvo/agents/machines/${encodeURIComponent(machineId)}`,
	KONVO_AGENTS_MACHINE_DISCONNECT: (machineId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/disconnect`,
	KONVO_AGENTS_THREADS: (machineId: string) => `/konvo/agents/machines/${encodeURIComponent(machineId)}/threads`,
	KONVO_AGENTS_WATCH: (machineId: string) => `/konvo/agents/machines/${encodeURIComponent(machineId)}/watch`,
	KONVO_AGENTS_BRANCHES: (machineId: string) => `/konvo/agents/machines/${encodeURIComponent(machineId)}/branches`,
	KONVO_AGENTS_FOLDERS: (machineId: string) => `/konvo/agents/machines/${encodeURIComponent(machineId)}/folders`,
	KONVO_AGENTS_REPOS: (machineId: string) => `/konvo/agents/machines/${encodeURIComponent(machineId)}/repos`,
	KONVO_AGENTS_PROJECTS: (machineId: string) => `/konvo/agents/machines/${encodeURIComponent(machineId)}/projects`,
	KONVO_AGENTS_CLOUD_HANDOFF: (machineId: string, threadId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/cloud-handoff`,
	KONVO_AGENTS_THREAD_PROJECT: (machineId: string, threadId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/project`,
	KONVO_AGENTS_PROJECT_PUSH: (machineId: string, projectId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/projects/${encodeURIComponent(projectId)}/push`,
	KONVO_AGENTS_PROJECT: (machineId: string, projectId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/projects/${encodeURIComponent(projectId)}`,
	KONVO_AGENTS_THREAD_SOURCE: (machineId: string, threadId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/source`,
	KONVO_AGENTS_THREAD: (machineId: string, threadId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}`,
	KONVO_AGENTS_THREAD_MESSAGES: (machineId: string, threadId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/messages`,
	KONVO_AGENTS_THREAD_INTERRUPT: (machineId: string, threadId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/interrupt`,
	KONVO_AGENTS_THREAD_QUEUE: (machineId: string, threadId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/queued-runs`,
	KONVO_AGENTS_THREAD_DEV_SERVER_STOP: (machineId: string, threadId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/dev-servers/stop`,
	KONVO_AGENTS_THREAD_COMPACT: (machineId: string, threadId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/compact`,
	KONVO_AGENTS_THREAD_REWIND: (machineId: string, threadId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/rewind`,
	KONVO_AGENTS_THREAD_RESTART: (machineId: string, threadId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/restart`,
	KONVO_AGENTS_THREAD_TIME: (machineId: string, threadId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/time`,
	KONVO_AGENTS_TIME_CLIENTS: '/konvo/agents/time/clients',
	KONVO_AGENTS_TIME_ESTIMATE: '/konvo/agents/time/estimate',
	KONVO_AGENTS_THREAD_SETTLE: (machineId: string, threadId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/settle`,
	KONVO_AGENTS_THREAD_RENAME: (machineId: string, threadId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/rename`,
	KONVO_AGENTS_THREAD_ARCHIVE: (machineId: string, threadId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/archive`,
	KONVO_AGENTS_ARCHIVED: (machineId: string) => `/konvo/agents/machines/${encodeURIComponent(machineId)}/archived`,
	KONVO_AGENTS_ATTACHMENT: (machineId: string, threadId: string, attachmentId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/attachments/${encodeURIComponent(attachmentId)}`,
	// `reply` (the reply that links the file) only tells the caches apart: an agent that re-shoots a
	// picture under the same name shows it in a new reply, which must read the file again.
	KONVO_AGENTS_MEDIA: (machineId: string, threadId: string, path: string, reply?: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/media?path=${encodeURIComponent(path)}${replyParam(reply)}`,
	KONVO_AGENTS_DOCUMENT: (machineId: string, threadId: string, path: string, reply?: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/document?path=${encodeURIComponent(path)}${replyParam(reply)}`,
	KONVO_AGENTS_DOCUMENT_ASSET: (machineId: string, threadId: string, path: string, asset: string, reply?: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/document-asset?path=${encodeURIComponent(path)}&asset=${encodeURIComponent(asset)}${replyParam(reply)}`,
	KONVO_AGENTS_WEB_PICTURES: '/konvo/agents/web-pictures',
	KONVO_HTML_LIBRARY: '/konvo/html/library',
	KONVO_HTML_WEB_PICTURES: '/konvo/html/web-pictures',
	KONVO_SITE_ICON: '/konvo/site-icon',
	KONVO_AGENTS_DIFF: (machineId: string, threadId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/diff`,
	KONVO_AGENTS_SEARCH: (machineId: string) => `/konvo/agents/machines/${encodeURIComponent(machineId)}/search`,
	KONVO_AGENTS_TERMINALS_SESSION: (machineId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/terminals/session`,
	KONVO_AGENTS_FILES: (machineId: string, threadId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/files`,
	KONVO_AGENTS_FILE_REFS: (machineId: string, threadId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/files/refs`,
	KONVO_AGENTS_FILE_SEARCH: (machineId: string, threadId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/files/search`,
	KONVO_AGENTS_FILE_TEXT: (machineId: string, threadId: string, path: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/files/text?path=${encodeURIComponent(path)}`,
	KONVO_AGENTS_FILE_RAW: (machineId: string, threadId: string, path: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/files/raw?path=${encodeURIComponent(path)}`,
	KONVO_AGENTS_SUBAGENT: (machineId: string, threadId: string, subagentId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/subagents/${encodeURIComponent(subagentId)}`,
	KONVO_AGENTS_SUBAGENT_PICTURE: (machineId: string, threadId: string, subagentId: string, path: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/subagents/${encodeURIComponent(subagentId)}/picture?path=${encodeURIComponent(path)}`,
	KONVO_AGENTS_THREAD_APPROVAL: (machineId: string, threadId: string, requestId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/approvals/${encodeURIComponent(requestId)}`,
	KONVO_AGENTS_SEEN: '/konvo/agents/seen',
	KONVO_AGENTS_OPENED: '/konvo/agents/opened',
	KONVO_AGENTS_PINS: '/konvo/agents/pins',
	KONVO_AGENTS_SNOOZES: '/konvo/agents/snoozes',
	KONVO_AGENTS_PROJECT_ICONS: '/konvo/agents/project-icons',
	KONVO_AGENTS_QUEUED: '/konvo/agents/queued',
	KONVO_AGENTS_THREAD_QUEUED: (machineId: string, threadId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/queued`,
	KONVO_AGENTS_THREAD_QUESTION: (machineId: string, threadId: string, requestId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/questions/${encodeURIComponent(requestId)}`,
	KONVO_AGENTS_THREAD_QUESTION_DISMISS: (machineId: string, threadId: string, requestId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/questions/${encodeURIComponent(requestId)}/dismiss`,
	KONVO_AGENTS_THREAD_SECRET: (machineId: string, threadId: string, requestId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/secrets/${encodeURIComponent(requestId)}`,
	KONVO_AGENTS_THREAD_QUESTION_ASK: (machineId: string, threadId: string, requestId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/questions/${encodeURIComponent(requestId)}/ask`,
	KONVO_AGENTS_THREAD_ASKS: (machineId: string, threadId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/asks`,
	KONVO_AGENTS_ASK: (askId: string) => `/konvo/agents/asks/${encodeURIComponent(askId)}`,
	KONVO_AGENTS_ASK_ANSWER: (askId: string) => `/konvo/agents/asks/${encodeURIComponent(askId)}/answer`,
	// Team threads (AgentShares).
	KONVO_AGENTS_SHARED: '/konvo/agents/shared',
	KONVO_AGENTS_SHARES: '/konvo/agents/shares',
	KONVO_AGENTS_MACHINE_SETTINGS: (machineId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/settings`,
	// The Agents browser (AgentBrowser): a tunnel session, the machine's dev servers, who opened what.
	KONVO_AGENTS_BROWSER_SESSION: (machineId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/browser/session`,
	KONVO_AGENTS_BROWSER_SERVERS: (machineId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/browser/servers`,
	KONVO_AGENTS_BROWSER_LOG: (machineId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/browser/log`,
	KONVO_AGENTS_SHARING_STOP: (machineId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/sharing/stop`,
	KONVO_AGENTS_THREAD_SHARE: (machineId: string, threadId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/share`,
	KONVO_AGENTS_THREAD_SHARE_PERSON: (machineId: string, threadId: string, userId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/share/people/${encodeURIComponent(userId)}`,
	KONVO_AGENTS_THREAD_SHARE_COMMENTS: (machineId: string, threadId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/share/comments`,
	KONVO_AGENTS_THREAD_SHARE_COMMENT: (
		machineId: string,
		threadId: string,
		commentId: string,
		action: 'resolve' | 'send',
	) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/share/comments/${encodeURIComponent(commentId)}/${action}`,
	KONVO_AGENTS_THREAD_SHARE_PROMPTS: (machineId: string, threadId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/share/prompts`,
	KONVO_AGENTS_THREAD_SHARE_PROMPT: (machineId: string, threadId: string, promptId: string) =>
		`/konvo/agents/machines/${encodeURIComponent(machineId)}/threads/${encodeURIComponent(threadId)}/share/prompts/${encodeURIComponent(promptId)}`,
	KONVO_CLIENT_HELLO: '/konvo/client/hello',
	KONVO_CLIENT_POLICY: '/konvo/client/policy',
	KONVO_INSIGHTS_CLIENT_POLICY: '/konvo/insights/client-policy',
	// KONVO: getting started with the Assistant (Home card, DM starter cards) and messages from Konvo.
	KONVO_ASSISTANT_GETTING_STARTED: '/konvo/assistant/getting-started',
	KONVO_ASSISTANT_GETTING_STARTED_DISMISS: '/konvo/assistant/getting-started/dismiss',
	KONVO_ASSISTANT_GETTING_STARTED_DM_SHOWN: '/konvo/assistant/getting-started/dm-shown',
	KONVO_ASSISTANT_NUDGE_SETTINGS: '/konvo/assistant/nudges/settings',
	KONVO_MAIL_CONTACTS: '/konvo/mail/contacts',
	KONVO_MAIL_SEND: '/konvo/mail/send',
	KONVO_MAIL_MESSAGE_READ: (messageId: string) => `/konvo/mail/messages/${encodeURIComponent(messageId)}/read`,
	KONVO_MAIL_MESSAGE_FLAG: (messageId: string) => `/konvo/mail/messages/${encodeURIComponent(messageId)}/flag`,
	KONVO_MAIL_MESSAGE_PIN: (messageId: string) => `/konvo/mail/messages/${encodeURIComponent(messageId)}/pin`,
	KONVO_MAIL_MESSAGE_MOVE: (messageId: string) => `/konvo/mail/messages/${encodeURIComponent(messageId)}/move`,
	KONVO_MAIL_MESSAGES_MOVE: '/konvo/mail/messages/move-many',
	KONVO_MAIL_FYI_RULE: (group: string) => `/konvo/mail/fyi/rules/${encodeURIComponent(group)}`,
	KONVO_MAIL_FYI_SUMMARY: '/konvo/mail/fyi/summary',
	KONVO_MAIL_MESSAGE_REPLY: (messageId: string) => `/konvo/mail/messages/${encodeURIComponent(messageId)}/reply`,
	KONVO_MAIL_MESSAGE_SUMMARY: (messageId: string) => `/konvo/mail/messages/${encodeURIComponent(messageId)}/summary`,
	KONVO_MAIL_MESSAGE_EVENT: (messageId: string) => `/konvo/mail/messages/${encodeURIComponent(messageId)}/event`,
	KONVO_MAIL_ATTACHMENT: (messageId: string, attachmentId: string) =>
		`/konvo/mail/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachmentId)}`,
	KONVO_MAIL_DRAFTS: '/konvo/mail/drafts',
	KONVO_MAIL_DRAFT: (draftId: string) => `/konvo/mail/drafts/${encodeURIComponent(draftId)}`,
	KONVO_MAIL_DRAFT_SEND: (draftId: string) => `/konvo/mail/drafts/${encodeURIComponent(draftId)}/send`,
	KONVO_MAIL_DRAFT_ATTACHMENTS: (draftId: string) => `/konvo/mail/drafts/${encodeURIComponent(draftId)}/attachments`,
	KONVO_MAIL_DRAFT_ATTACHMENT: (draftId: string, attachmentId: string) =>
		`/konvo/mail/drafts/${encodeURIComponent(draftId)}/attachments/${encodeURIComponent(attachmentId)}`,
	KONVO_MAIL_DRAFT_UPLOAD_SESSIONS: (draftId: string) =>
		`/konvo/mail/drafts/${encodeURIComponent(draftId)}/attachments/sessions`,
	KONVO_MAIL_DRAFT_UPLOAD_SESSION: (draftId: string, sessionId: string) =>
		`/konvo/mail/drafts/${encodeURIComponent(draftId)}/attachments/sessions/${encodeURIComponent(sessionId)}`,
	KONVO_MAIL_COMPOSE_ASSIST: '/konvo/mail/compose-assist',
	KONVO_MAIL_SIGNATURES: '/konvo/mail/signatures',
	KONVO_MAIL_SIGNATURE_SOURCES: '/konvo/mail/signatures/sources',
	KONVO_MAIL_FOLDERS: '/konvo/mail/folders',
	KONVO_MAIL_FILING: '/konvo/mail/filing',
	KONVO_MAIL_FORWARD_BUNDLE: '/konvo/mail/forward-bundle',
	KONVO_REMINDERS: '/konvo/reminders',
	KONVO_REMINDER: (reminderId: string) => `/konvo/reminders/${encodeURIComponent(reminderId)}`,
	KONVO_SCHEDULED: '/konvo/scheduled',
	KONVO_SCHEDULED_PROMISE: (commitmentId: string) => `/konvo/scheduled/promises/${encodeURIComponent(commitmentId)}`,
	/** The "From Konvo" list: ready corner suggestions and engine briefs/nudges (ReadyListResponseSchema). */
	KONVO_CORNER_LIST: '/konvo/corner/list',
	/** Sends a ready card's written reply as is and marks the suggestion used (no body). */
	/** Answers "Am I getting in your way?" (AskChangeRequestSchema → AskChangeResponseSchema). */
	KONVO_CORNER_ASK_CHANGE: '/konvo/corner/ask-change',
	/** Puts back what the last answer to "Am I getting in your way?" changed (`{undo_token}`). */
	KONVO_CORNER_ASK_CHANGE_UNDO: '/konvo/corner/ask-change/undo',
	KONVO_CORNER_SEND_REPLY: (suggestionId: string) =>
		`/konvo/corner/suggestions/${encodeURIComponent(suggestionId)}/send-reply`,
	KONVO_SCHEDULED_MAIL_CANCEL: (messageId: string) => `/konvo/scheduled/mail/${encodeURIComponent(messageId)}/cancel`,
	KONVO_HOME_HIDDEN: '/konvo/home/hidden',
	KONVO_CARD_REPORT: (cardId: string) => `/konvo/cards/${encodeURIComponent(cardId)}/report`,
	KONVO_CARD_ACTION: (cardId: string, action: string) =>
		`/konvo/cards/${encodeURIComponent(cardId)}/actions/${encodeURIComponent(action)}`,
	KONVO_CARD_DRAFT: (cardId: string) => `/konvo/cards/${encodeURIComponent(cardId)}/draft`,
	READ_STATES_ACK: '/read-states/ack',
	DSA_REPORT_EMAIL_SEND: '/reports/dsa/email/send',
	DSA_REPORT_EMAIL_VERIFY: '/reports/dsa/email/verify',
	DSA_REPORT_CREATE: '/reports/dsa',
	GIFS_FEATURED: '/gifs/featured',
	GIFS_REGISTER_SHARE: '/gifs/register-share',
	GIFS_SEARCH: '/gifs/search',
	GIFS_SUGGEST: '/gifs/suggest',
	GIFS_TRENDING: '/gifs/trending',
	UNFURL: '/unfurl',
	USER_CHANNELS: '/users/@me/channels',
	USER_CHANNEL_PIN: (channelId: string) => `/users/@me/channels/${channelId}/pin`,
	USER_GUILDS_LIST: '/users/@me/guilds',
	USER_GUILDS: (guildId: string) => `/users/@me/guilds/${guildId}`,
	USER_GUILD_BULK_DELETE_MY_MESSAGES: (guildId: string) => `/users/@me/guilds/${guildId}/messages/bulk-delete-mine`,
	CHANNEL_BULK_DELETE_MY_MESSAGES: (channelId: string) => `/channels/${channelId}/messages/bulk-delete-mine`,
	USER_ME: '/users/@me',
	USER_TERMS_ACCEPTANCE: '/users/@me/terms-acceptance',
	USER_MENTION: (messageId: string) => `/users/@me/mentions/${messageId}`,
	USER_MENTIONS: '/users/@me/mentions',
	USER_MENTIONS_READ: '/users/@me/mentions/read',
	USER_MFA_BACKUP_CODES: '/users/@me/mfa/backup-codes',
	USER_MFA_TOTP_DISABLE: '/users/@me/mfa/totp/disable',
	USER_MFA_TOTP_ENABLE: '/users/@me/mfa/totp/enable',
	USER_AUTHORIZED_IPS: '/users/@me/authorized-ips',
	USER_MFA_WEBAUTHN_CREDENTIALS: '/users/@me/mfa/webauthn/credentials',
	USER_MFA_WEBAUTHN_REGISTRATION_OPTIONS: '/users/@me/mfa/webauthn/credentials/registration-options',
	USER_MFA_WEBAUTHN_CREDENTIAL: (credentialId: string) => `/users/@me/mfa/webauthn/credentials/${credentialId}`,
	USER_PHONE_SEND_VERIFICATION: '/users/@me/phone/send-verification',
	USER_PHONE_INBOUND_CHALLENGE: '/users/@me/phone/inbound-challenge',
	USER_PHONE_VERIFY: '/users/@me/phone/verify',
	USER_EMAIL_CHANGE_START: '/users/@me/email-change/start',
	USER_EMAIL_CHANGE_RESEND_ORIGINAL: '/users/@me/email-change/resend-original',
	USER_EMAIL_CHANGE_VERIFY_ORIGINAL: '/users/@me/email-change/verify-original',
	USER_EMAIL_CHANGE_REQUEST_NEW: '/users/@me/email-change/request-new',
	USER_EMAIL_CHANGE_RESEND_NEW: '/users/@me/email-change/resend-new',
	USER_EMAIL_CHANGE_VERIFY_NEW: '/users/@me/email-change/verify-new',
	USER_EMAIL_CHANGE_APPLY: '/users/@me/email-change/apply',
	USER_EMAIL_CHANGE_BOUNCED_REQUEST_NEW: '/users/@me/email-change/bounced/request-new',
	USER_EMAIL_CHANGE_BOUNCED_RESEND_NEW: '/users/@me/email-change/bounced/resend-new',
	USER_EMAIL_CHANGE_BOUNCED_VERIFY_NEW: '/users/@me/email-change/bounced/verify-new',
	USER_PASSWORD_CHANGE_START: '/users/@me/password-change/start',
	USER_PASSWORD_CHANGE_RESEND: '/users/@me/password-change/resend',
	USER_PASSWORD_CHANGE_VERIFY: '/users/@me/password-change/verify',
	USER_PASSWORD_CHANGE_COMPLETE: '/users/@me/password-change/complete',
	USER_DISABLE: '/users/@me/disable',
	USER_DELETE: '/users/@me/delete',
	USER_BULK_DELETE_MY_MESSAGES: '/users/@me/messages/bulk-delete-mine',
	USER_HARVEST: '/users/@me/harvest',
	USER_HARVEST_FILTERED: '/users/@me/harvest/filtered',
	USER_HARVEST_LATEST: '/users/@me/harvest/latest',
	USER_HARVEST_STATUS: (harvestId: string) => `/users/@me/harvest/${harvestId}`,
	USER_PRELOAD_MESSAGES: '/users/@me/preload-messages',
	USER_NOTE: (userId: string) => `/users/@me/notes/${userId}`,
	USER_CHECK_TAG: '/users/check-tag',
	USER_PROFILE: (query = ME) => `/users/${query}/profile`,
	USER_RELATIONSHIP: (userId: string) => `/users/@me/relationships/${userId}`,
	USER_RELATIONSHIPS: '/users/@me/relationships',
	USER_RELATIONSHIPS_BULK_IGNORE: '/users/@me/relationships/bulk-ignore',
	USER_THEMES: '/users/@me/themes',
	USER_SAVED_MESSAGE: (messageId: string) => `/users/@me/saved-messages/${messageId}`,
	USER_SAVED_MESSAGES: '/users/@me/saved-messages',
	USER_ENTRANCE_SOUNDS: '/users/@me/entrance-sounds',
	USER_ENTRANCE_SOUND: (soundId: string) => `/users/@me/entrance-sounds/${soundId}`,
	USER_ENTRANCE_SOUND_SELECTIONS: '/users/@me/entrance-sound-selections',
	VOICE_CHANNEL_ENTRANCE_SOUND: (channelId: string) => `/voice/channels/${channelId}/entrance-sound`,
	USER_SCHEDULED_MESSAGES: '/users/@me/scheduled-messages',
	USER_SCHEDULED_MESSAGE: (messageId: string) => `/users/@me/scheduled-messages/${messageId}`,
	USER_FAVORITE_MEMES: (query = ME) => `/users/${query}/memes`,
	USER_FAVORITE_MEME: (query = ME, memeId: string) => `/users/${query}/memes/${memeId}`,
	USER_FAVORITE_GIFS_RESOLVE: (query = ME) => `/users/${query}/favorite-gifs/resolve`,
	CHANNEL_MESSAGE_FAVORITE_MEMES: (channelId: string, messageId: string) =>
		`/channels/${channelId}/messages/${messageId}/memes`,
	STREAM_PREVIEW: (streamKey: string) => `/streams/${streamKey}/preview`,
	STREAM_PREVIEW_UPLOAD_URL: (streamKey: string) => `/streams/${streamKey}/preview/upload-url`,
	USER_SETTINGS: '/users/@me/settings',
	USER_VOICE_ACTIVITY_SHARING: '/users/@me/settings/voice-activity-sharing',
	USER_GUILD_SETTINGS_ME: '/users/@me/guilds/@me/settings',
	USER_GUILD_SETTINGS: (guildId: string) => `/users/@me/guilds/${guildId}/settings`,
	USER_PUSH_SUBSCRIBE: '/users/@me/push/subscribe',
	USER_PUSH_ROTATE: '/users/@me/push/rotate',
	USER_PUSH_SUBSCRIPTIONS: '/users/@me/push/subscriptions',
	USER_PUSH_SUBSCRIPTION: (subscriptionId: string) => `/users/@me/push/subscriptions/${subscriptionId}`,
	PACKS: '/packs',
	PACK: (packId: string) => `/packs/${packId}`,
	PACK_CREATE: (packType: 'emoji' | 'sticker') => `/packs/${packType}`,
	PACK_INSTALL: (packId: string) => `/packs/${packId}/install`,
	PACK_EMOJIS: (packId: string) => `/packs/emojis/${packId}`,
	PACK_EMOJI: (packId: string, emojiId: string) => `/packs/emojis/${packId}/${emojiId}`,
	PACK_EMOJI_BULK: (packId: string) => `/packs/emojis/${packId}/bulk`,
	PACK_STICKERS: (packId: string) => `/packs/stickers/${packId}`,
	PACK_STICKER: (packId: string, stickerId: string) => `/packs/stickers/${packId}/${stickerId}`,
	PACK_STICKERS_BULK: (packId: string) => `/packs/stickers/${packId}/bulk`,
	PACK_INVITES: (packId: string) => `/packs/${packId}/invites`,
	WEBHOOK: (webhookId: string) => `/webhooks/${webhookId}`,
	REPORT_MESSAGE: '/reports/message',
	REPORT_USER: '/reports/user',
	REPORT_GUILD: '/reports/guild',
	DISCOVERY_GUILDS: '/discovery/guilds',
	DISCOVERY_CATEGORIES: '/discovery/categories',
	DISCOVERY_JOIN: (guildId: string) => `/discovery/guilds/${guildId}/join`,
	GUILD_DISCOVERY: (guildId: string) => `/guilds/${guildId}/discovery`,
	CONNECTIONS: '/users/@me/connections',
	CONNECTIONS_VERIFY_AND_CREATE: '/users/@me/connections/verify',
	CONNECTION: (type: string, connectionId: string) => `/users/@me/connections/${type}/${connectionId}`,
	CONNECTION_VERIFY: (type: string, connectionId: string) => `/users/@me/connections/${type}/${connectionId}/verify`,
	CONNECTIONS_REORDER: '/users/@me/connections/reorder',
} as const;
